/**
 * bb-cart-redirect.js
 * ───────────────────────────────────────────────────────────────────────────
 * Send the shopper to the cart page after anything is added to the cart.
 *
 * The theme's default with `cart_type: page` is to add via AJAX and stay put —
 * the cart bubble updates and a live region announces the add, but the shopper
 * is left on the product page. This redirects instead.
 *
 * It listens on the cart event rather than patching the add-to-cart button, so
 * it covers every path that adds: the product form, quick-add on cards, and the
 * Bower & Branch add-on modal (which adds the parent plus its add-ons in one
 * call). Verified event shape at runtime: { action, context, lines, promise }.
 *
 * Only `action === 'add'` redirects. Cart-page quantity edits and removals
 * dispatch 'update' / 'remove' and must not navigate — that would fight the
 * shopper mid-edit, and would also break the add-on reconcile pass, which
 * rewrites lines from the cart page itself.
 *
 * The redirect waits on `event.promise` and checks `detail.didError`:
 * product-form.js's own add path RESOLVES that promise even when Shopify
 * rejects the add (it sets `didError: true` rather than rejecting — see
 * product-form.js's #processAddToCart), so a plain try/catch around the await
 * would not have caught it. Checking didError leaves the shopper on the
 * product page with the theme's own error visible, rather than bouncing them
 * to an unchanged cart.
 */

import { StandardEvents } from '@shopify/events';
import { showCartOverlay, hideCartOverlay, cartUrl, alreadyOnCart } from '@theme/bb-cart-overlay';

/** One redirect per page, even if several adds resolve together. */
let navigating = false;

/** @param {Event & {action?: string, promise?: Promise<any>}} event */
async function onCartLinesUpdate(event) {
  if (event.action !== 'add') return;
  if (navigating || alreadyOnCart()) return;

  // Backstop: bb-cart-overlay's own listener covers plain product forms and
  // bb-addons raises it when its modal finalises, but a surface neither of them
  // knows about (quick-add on a card, an app block) still gets the curtain from
  // here. Idempotent — raising it twice is a no-op.
  showCartOverlay();

  try {
    // Resolves once the add has actually landed — but a Shopify-rejected add
    // ALSO resolves here (with detail.didError: true); it does not reject.
    // Only a genuine failure to build/read the cart payload afterward rejects
    // (see bb-addons' dispatchCartUpdate for one such case).
    //
    // Destructuring `detail` straight off the awaited value throws when a
    // dispatcher resolves its promise with no payload at all (undefined) —
    // every in-theme dispatcher provides one, but a third-party/app dispatcher
    // might not, and that throw used to be swallowed by the catch below into a
    // silent, undiagnosable "no redirect, no error" on the money path. Optional
    // chaining here reads as "no detail" rather than throwing.
    const result = await event.promise;
    if (result?.detail?.didError) {
      // Drop the curtain: the shopper stays here with the theme's own error
      // visible, and must be able to interact with the page to act on it.
      hideCartOverlay();
      return; // the theme surfaces the error in place — don't navigate away from it
    }
  } catch {
    // Drop the curtain: the shopper stays here with the theme's own error
    // visible, and must be able to interact with the page to act on it.
    hideCartOverlay();
    return; // the theme surfaces the error in place — don't navigate away from it
  }

  if (navigating || alreadyOnCart()) return;
  navigating = true;
  window.location.assign(cartUrl());
}

if (typeof document !== 'undefined') {
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
}
